/**
 * StudentProfileModal — Personalization Phase 1.
 *
 * Lets a student set:
 *   - Avatar (from a school-safe preset gallery — uploads disabled in P1)
 *   - Business name (60 chars max — server-validated)
 *   - Team role (Leader · Marketing · Finance · Operations)
 *
 * Persists via PUT /api/auth/profile. Calls onSaved(updatedUser) so the
 * parent (Dashboard) can refresh its hero immediately.
 *
 * School-safe by construction:
 *   - No URL or file inputs for avatar — only the catalog from
 *     GET /api/profile/avatar-presets. Backend rejects anything else.
 *   - Business name + role are private to this device until the student
 *     uses an explicit Share/Export action (which doesn't exist yet in P1).
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { X, Check, Loader2, Building2, Crown, Megaphone, Calculator, Cog } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TEAM_ROLES = [
  {
    key: "leader",
    label: "Leader",
    icon: Crown,
    blurb: "Sets vision, makes the final call.",
    color: "purple",
  },
  {
    key: "marketing",
    label: "Marketing",
    icon: Megaphone,
    blurb: "Builds the brand and finds customers.",
    color: "pink",
  },
  {
    key: "finance",
    label: "Finance",
    icon: Calculator,
    blurb: "Owns the numbers — costs, prices, profit.",
    color: "emerald",
  },
  {
    key: "operations",
    label: "Operations",
    icon: Cog,
    blurb: "Makes things run smoothly day-to-day.",
    color: "blue",
  },
];

const COLORS = {
  purple: "border-purple-300 bg-purple-50 text-purple-700",
  pink: "border-pink-300 bg-pink-50 text-pink-700",
  emerald: "border-emerald-300 bg-emerald-50 text-emerald-700",
  blue: "border-blue-300 bg-blue-50 text-blue-700",
};
const COLORS_ACTIVE = {
  purple: "border-purple-500 bg-purple-100 ring-2 ring-purple-300",
  pink: "border-pink-500 bg-pink-100 ring-2 ring-pink-300",
  emerald: "border-emerald-500 bg-emerald-100 ring-2 ring-emerald-300",
  blue: "border-blue-500 bg-blue-100 ring-2 ring-blue-300",
};

export default function StudentProfileModal({ open, user, onClose, onSaved }) {
  const [presets, setPresets] = useState([]);
  const [avatarKey, setAvatarKey] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [teamRole, setTeamRole] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setError("");
    // Initialize from user
    const av = (user?.avatar_url || "").replace(/^preset:/, "");
    setAvatarKey(av);
    setBusinessName(user?.business_name || "");
    setTeamRole(user?.team_role || "");

    // Fetch presets (cheap & static — fine to refetch each time the modal opens)
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/profile/avatar-presets`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        if (!cancelled) setPresets(res.data.presets || []);
      } catch {
        if (!cancelled) setPresets([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, user]);

  // Esc closes
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape" && !saving) onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, saving, onClose]);

  if (!open) return null;

  const trimmedName = businessName.trim();
  const tooLong = trimmedName.length > 60;
  const canSave = !saving && !tooLong;

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      const res = await axios.put(
        `${API}/auth/profile`,
        {
          business_name: trimmedName || null,
          team_role: teamRole || null,
          avatar_url: avatarKey || null,
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      onSaved?.(res.data);
      onClose?.();
    } catch (err) {
      setError(err.response?.data?.detail || "Couldn't save your profile.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[180] bg-slate-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-2 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="profile-title"
      data-testid="student-profile-modal"
      onClick={saving ? undefined : onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden border border-purple-200 max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-amber-50 to-pink-50">
          <h2 id="profile-title" className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-amber-600" aria-hidden="true" />
            Your Profile
          </h2>
          {!saving && (
            <button
              onClick={onClose}
              className="p-1 rounded-lg hover:bg-white/60"
              aria-label="Close profile"
              data-testid="student-profile-close"
            >
              <X className="w-5 h-5 text-gray-600" aria-hidden="true" />
            </button>
          )}
        </div>

        <div className="overflow-y-auto px-5 py-4 space-y-5 flex-1">
          {/* Avatar gallery */}
          <section>
            <h3 className="font-semibold text-sm text-gray-900 mb-1">Pick an avatar</h3>
            <p className="text-xs text-gray-500 mb-2">
              Choose how you'll show up. (No uploads in this version — keeps it school safe.)
            </p>
            <div
              className="grid grid-cols-4 sm:grid-cols-6 gap-2"
              role="radiogroup"
              aria-label="Avatar"
              data-testid="avatar-presets-grid"
            >
              {presets.map((p) => {
                const selected = avatarKey === p.key;
                return (
                  <button
                    key={p.key}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    onClick={() => setAvatarKey(p.key)}
                    data-testid={`avatar-preset-${p.key}`}
                    className={`relative aspect-square rounded-xl border-2 flex items-center justify-center text-3xl transition-transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-amber-300 ${
                      selected
                        ? "border-amber-500 ring-2 ring-amber-300 shadow-md"
                        : "border-gray-200 hover:border-amber-300"
                    }`}
                    style={{ background: p.bg }}
                    title={p.label}
                  >
                    <span aria-hidden="true">{p.emoji}</span>
                    <span className="sr-only">{p.label}</span>
                    {selected && (
                      <span className="absolute -top-1 -right-1 bg-emerald-500 text-white rounded-full w-5 h-5 flex items-center justify-center">
                        <Check className="w-3 h-3" aria-hidden="true" />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </section>

          {/* Business name */}
          <section>
            <label
              htmlFor="profile-business-name"
              className="block font-semibold text-sm text-gray-900 mb-1"
            >
              Business name
            </label>
            <p className="text-xs text-gray-500 mb-2">
              We'll use this in your dashboard, exports, and when LemonBot talks to you.
            </p>
            <input
              id="profile-business-name"
              type="text"
              value={businessName}
              maxLength={60}
              onChange={(e) => setBusinessName(e.target.value)}
              placeholder="Lemon Sparks"
              className="w-full px-3 py-2 rounded-lg border-2 border-gray-200 focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-200"
              data-testid="profile-business-name-input"
            />
            <div className="flex justify-between text-[11px] mt-1">
              <span className={tooLong ? "text-red-600" : "text-gray-400"}>
                {tooLong ? "Too long — keep it under 60 characters." : "60 characters max"}
              </span>
              <span className="text-gray-400 tabular-nums">{trimmedName.length}/60</span>
            </div>
          </section>

          {/* Role selection */}
          <section>
            <h3 className="font-semibold text-sm text-gray-900 mb-1">Pick your role</h3>
            <p className="text-xs text-gray-500 mb-2">
              You can change this any time. LemonBot tailors its tips to your role.
            </p>
            <div
              className="grid grid-cols-2 gap-2"
              role="radiogroup"
              aria-label="Team role"
              data-testid="team-role-grid"
            >
              {TEAM_ROLES.map((r) => {
                const selected = teamRole === r.key;
                const Icon = r.icon;
                return (
                  <button
                    key={r.key}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    onClick={() => setTeamRole(r.key)}
                    data-testid={`team-role-${r.key}`}
                    className={`text-left rounded-xl border-2 px-3 py-2.5 transition-all focus:outline-none focus:ring-2 focus:ring-amber-300 ${
                      selected ? COLORS_ACTIVE[r.color] : COLORS[r.color]
                    }`}
                  >
                    <div className="flex items-center gap-2 font-bold text-sm">
                      <Icon className="w-4 h-4" aria-hidden="true" />
                      {r.label}
                    </div>
                    <p className="text-[11px] mt-0.5 leading-tight">{r.blurb}</p>
                  </button>
                );
              })}
            </div>
          </section>

          {error && (
            <div
              className="p-3 rounded-lg text-sm bg-red-50 text-red-700 border border-red-200"
              data-testid="profile-error"
            >
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-gray-700 hover:bg-white border border-gray-300 disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!canSave}
            data-testid="profile-save-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-gradient-to-r from-amber-500 to-pink-500 hover:opacity-95 disabled:bg-gray-300 disabled:from-gray-300 disabled:to-gray-300 disabled:cursor-not-allowed text-white"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Check className="w-4 h-4" />
            )}
            {saving ? "Saving…" : "Save profile"}
          </button>
        </div>
      </div>
    </div>
  );
}
